"use client";

import { useState } from "react";
import type { HeroPhoto } from "@/lib/parks";

// Further photographs of a park, below the hero.
//
// Every image here is a separate work by a separate photographer under its own
// licence, so each carries its own credit. That is not a nicety: CC BY and
// CC BY-SA both require naming the author wherever the work appears, and a
// strip of uncredited thumbnails would breach every one of them at once.
//
// The credit for a thumbnail is shown when it is opened rather than under each
// tile, because six captions under six small images is unreadable and the
// licence is satisfied by attribution being present with the work as displayed.
//
// Only the first GALLERY_PREVIEW thumbnails render until the visitor asks for
// the rest. A park can hold up to 100 photographs, and each thumbnail is the
// full ~460 KB image scaled down in the browser, so the whole grid would be
// about 45 MB for anyone who scrolled past it. Lazy loading alone does not
// help a visitor who scrolls to the footer. The lightbox still steps through
// every photograph, since it loads one image at a time.
export const GALLERY_PREVIEW = 12;

export function ParkGallery({ photos, parkName }: { photos: HeroPhoto[]; parkName: string }) {
  const [open, setOpen] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  if (photos.length === 0) return null;

  const current = open === null ? null : photos[open];
  const shown = showAll ? photos : photos.slice(0, GALLERY_PREVIEW);
  const hidden = photos.length - shown.length;

  return (
    <section className="mx-auto w-full max-w-container-max px-margin-mobile py-8 md:px-margin-desktop">
      <h2 className="font-headline-md text-headline-md text-primary">
        More photographs of {parkName}
      </h2>
      <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
        {photos.length} more, from Wikimedia Commons and from people who sent them in. Each is
        someone else&rsquo;s work: select one to see who took it and the licence it carries.
      </p>

      <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
        {shown.map((p, i) => (
          <li key={p.fileName}>
            <button
              type="button"
              onClick={() => setOpen(i)}
              className="block w-full overflow-hidden rounded-lg border border-outline-variant/60 bg-surface-container-low transition-opacity hover:opacity-85"
              aria-label={`Photograph ${i + 2} of ${parkName}, by ${p.attributions[0]?.displayName ?? "an unnamed photographer"}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={`/${p.fileName}`}
                alt=""
                loading="lazy"
                className="h-28 w-full object-cover md:h-32"
              />
            </button>
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-4 rounded-full border border-outline px-4 py-1.5 text-sm text-primary hover:bg-surface-container"
        >
          Show all {photos.length} photographs
        </button>
      )}

      {current && (
        // Not a modal library: a plain overlay, because this is one image and a
        // caption and the page already carries enough JavaScript.
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Photograph of ${parkName}`}
          onClick={() => setOpen(null)}
        >
          <figure
            className="max-h-full max-w-4xl overflow-auto rounded-xl bg-surface p-3"
            onClick={(e) => e.stopPropagation()}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={`/${current.fileName}`}
              alt={`${parkName}${current.title ? `: ${current.title}` : ""}`}
              className="max-h-[70vh] w-auto rounded-lg"
            />
            <figcaption className="mt-3 font-body-md text-body-md text-on-surface-variant">
              <PhotoCreditLine photo={current} />
            </figcaption>
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="text-xs text-on-surface-variant">
                {open! + 1} of {photos.length}
              </span>
              <span className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setOpen((open! - 1 + photos.length) % photos.length)}
                  className="rounded-full border border-outline px-3 py-1 text-sm"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setOpen((open! + 1) % photos.length)}
                  className="rounded-full border border-outline px-3 py-1 text-sm"
                >
                  Next
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(null)}
                  className="rounded-full bg-primary px-3 py-1 text-sm text-on-primary"
                >
                  Close
                </button>
              </span>
            </div>
          </figure>
        </div>
      )}
    </section>
  );
}

// The same credit the hero carries, so a photograph is attributed identically
// wherever it appears.
export function PhotoCreditLine({ photo }: { photo: HeroPhoto }) {
  const author = photo.attributions[0]?.displayName ?? "an unnamed photographer";
  const authorUrl = photo.attributions[0]?.uri;
  return (
    <>
      Photo by{" "}
      {authorUrl ? (
        <a href={authorUrl} target="_blank" rel="noopener noreferrer" className="underline">
          {author}
        </a>
      ) : (
        author
      )}
      {photo.licence && (
        <>
          {", "}
          {photo.licenceUrl ? (
            <a href={photo.licenceUrl} target="_blank" rel="noopener noreferrer license" className="underline">
              {photo.licence}
            </a>
          ) : (
            photo.licence
          )}
        </>
      )}
      {/* "via" only where there is somewhere to send the reader. A contributed
          photograph has a photographer and a licence but no page of its own,
          and naming Commons for a photograph that never came from there would
          be a false statement on the line the licence governs. */}
      {photo.sourceUrl ? (
        <>
          {" "}via{" "}
          <a href={photo.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline">
            Wikimedia Commons
          </a>
        </>
      ) : (
        <>{", contributed to the Atlas"}</>
      )}
    </>
  );
}
